<script setup>
import { ref, computed, onMounted, onActivated } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';

import { useBase, hora, MONO, CHIP_ESTADO } from './base';

import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import FiltroSelect from './FiltroSelect.vue';

import SandboxAPI from 'dashboard/api/fandata/sandbox';

// T-186, tela 5a — o momento em que alguém assume o que muda para o torcedor.
//
// Três decisões do desenho mandam aqui:
// 1. Ninguém publica sem ver o antes ao lado do depois. A lista é o corpo da tela, não um resumo.
// 2. O teste fica na MESMA tela, porque testar em outra aba não conta: quem decide precisa ter
//    visto o Bira responder com o rascunho, com o torcedor que ele escolheu.
// 3. Publicar vale só para conversas novas. Quem está falando com o Bira agora termina no
//    conteúdo de hoje, e o rodapé diz isso no instante da decisão.
const { t } = useI18n();
const { irPara } = useBase();
const route = useRoute();

// Voltar tem que devolver a pessoa para onde ela estava. Quem chega da tela de comportamento
// volta para lá; o padrão continua sendo a edição das respostas.
// A tela é a mesma para os dois lados, e quem diz de qual se trata é o ENDEREÇO. Era um
// parâmetro na URL, e aí `base/publicar` publicava a voz se apresentando como a da base.
const veioDoComportamento = computed(
  () => route.name === 'capitao_bira_voz_publicar'
);

// Depois de publicar não se volta para o editor: o rascunho acabou de virar versão e não há mais
// o que editar ali. Volta para o ÍNDICE do lado de onde se veio, que é onde a publicação nova
// aparece na lista.
const indiceDaOrigem = computed(() =>
  veioDoComportamento.value ? 'capitao_calibracao' : 'capitao_base'
);

const voltarPara = computed(() =>
  veioDoComportamento.value
    ? 'capitao_base_comportamento'
    : 'capitao_base_editar'
);

const FRASE_LIBERADORA = 'publicar agora';

const carregando = ref(true);
const respostas = ref({ aberto: false, itens: [] });
const comportamento = ref({ campos: [], antes: {}, depois: {} });
const publicacao = ref({});
const catalogo = ref({ planos: [], situacoes: { grupos: [], descricoes: [] } });
const rotulo = ref('');

const teste = ref({ texto: '', plano: '', situacao: '' });
const rodando = ref(false);
const resultado = ref(null);
const testes = ref(0);
// O outro assunto pode ter rascunho aberto e ele NÃO entra neste teste: cada um publica sozinho.
// Testar com ele prometeria uma resposta que ninguém vai receber enquanto ele não subir.
const outroRascunho = ref(null);

const confirmando = ref(null);
const frase = ref('');
const publicando = ref(false);

// ⚠️ `reset-base` nos <input> e `m-0 border-solid` nos <select>/<textarea> não são enfeite.
// O `_base.scss` estiliza todo campo de formulário por seletor de elemento com uma pilha de
// `:not()`, e a especificidade disso vence qualquer classe do Tailwind: sem o reset, todo input
// desta tela volta a ter largura cheia, margem de 16px embaixo e `border-none` com outline no
// lugar da borda. Era isso que sumia com as bordas e esticava o campo de busca.

const respostasMudadas = computed(
  () =>
    (respostas.value.entraram || 0) +
    (respostas.value.mudaram || 0) +
    (respostas.value.saem || 0)
);

// Uma publicação leva as respostas E o jeito de falar. Contar só as respostas fazia a tela dizer
// "não há nada para publicar" para um rascunho que mudou o tom do Bira.
// A tela publica UM lado: o de onde a pessoa veio. Contar o outro faria o botão prometer o que
// a publicação não vai entregar, que é o defeito que a separação existe para acabar.
const pendentes = computed(() =>
  veioDoComportamento.value
    ? comportamento.value.campos.length
    : respostasMudadas.value
);

const temRascunho = computed(() =>
  veioDoComportamento.value
    ? comportamento.value.campos.length > 0
    : respostas.value.aberto
);
const testado = computed(() => Boolean(publicacao.value.testado));

// Resposta sem vetor EXISTE mas não é encontrada pela busca do Bira, e some só na hora em que
// o torcedor pergunta. A indexação leva uns 10 minutos numa base de 350 (limite da Voyage),
// então publicar logo depois de importar coloca no ar uma base que o Bira não enxerga inteira.
const indexacao = computed(() => publicacao.value.indexacao || {});
const naoIndexadas = computed(() => indexacao.value.pendentes || 0);
const naJanela = computed(() => Boolean(publicacao.value.dentro_da_janela));

const opcoesPlano = computed(() => [
  { valor: '', rotulo: t('BASE_PUBLICAR.QUALQUER') },
  ...(catalogo.value.planos || []).map(plano => ({
    valor: plano.nome,
    rotulo: plano.nome,
  })),
]);

const opcoesSituacao = computed(() => [
  { valor: '', rotulo: t('BASE_PUBLICAR.QUALQUER') },
  ...(catalogo.value.situacoes?.descricoes || []).map(descricao => ({
    valor: descricao,
    rotulo: descricao,
  })),
]);

const carregar = async () => {
  carregando.value = true;
  try {
    const { data } = await SandboxAPI.diff(
      'faq',
      veioDoComportamento.value ? 'comportamento' : 'base'
    );
    respostas.value = data.respostas || { aberto: false, itens: [] };
    comportamento.value = data.comportamento || {
      campos: [],
      antes: {},
      depois: {},
    };
    publicacao.value = data.publicacao || {};
    catalogo.value = data.torcedor || catalogo.value;
    rotulo.value = respostas.value.rotulo || '';
  } catch (error) {
    useAlert(t('BASE_PUBLICAR.ERRO'));
  } finally {
    carregando.value = false;
  }
};

const salvarRotulo = async () => {
  try {
    await SandboxAPI.salvar({ rotulo: rotulo.value });
  } catch (error) {
    useAlert(t('BASE_PUBLICAR.ERRO_ROTULO'));
  }
};

const rodarTeste = async () => {
  if (!teste.value.texto.trim()) return;
  rodando.value = true;
  try {
    const { data } = await SandboxAPI.testar({
      escopo: veioDoComportamento.value ? 'comportamento' : 'base',
      texto: teste.value.texto,
      plano: teste.value.plano,
      situacao: teste.value.situacao,
    });
    resultado.value = data;
    outroRascunho.value = data.outro_rascunho || null;
    testes.value += 1;
    // O "já testou" é do servidor, não deste contador: é ele que libera o botão de publicar.
    publicacao.value = {
      ...publicacao.value,
      ...(data.estado?.publicacao || {}),
    };
  } catch (error) {
    useAlert(t('BASE_PUBLICAR.ERRO_TESTE'));
  } finally {
    rodando.value = false;
  }
};

// O aviso diz o que subiu, e não um "publicado" genérico: um rascunho que só mudou o tom não
// publicou resposta nenhuma, e dizer "336 respostas" ali seria mentira tranquilizadora.
const avisoDePublicacao = () => {
  const quantas = respostasMudadas.value;
  const voz = comportamento.value.campos.length > 0;
  if (quantas && voz) return t('BASE_PUBLICAR.PUBLICADO_TUDO', { n: quantas });
  return voz
    ? t('BASE_PUBLICAR.PUBLICADO_VOZ')
    : t('BASE_PUBLICAR.PUBLICADO', { n: quantas });
};

const publicarAgora = async () => {
  confirmando.value = null;
  publicando.value = true;
  try {
    await SandboxAPI.publicar(
      veioDoComportamento.value ? 'comportamento' : 'base'
    );
    useAlert(avisoDePublicacao());
    irPara(indiceDaOrigem.value);
  } catch (error) {
    useAlert(t('BASE_PUBLICAR.ERRO_PUBLICAR'));
  } finally {
    publicando.value = false;
  }
};

// As duas guardas do desenho, nesta ordem: teste primeiro (5b), janela depois (5c). Nenhuma
// bloqueia; as duas obrigam a pessoa a dizer que sabe o que está fazendo.
const tentarPublicar = () => {
  // A indexação vem antes do teste: testar uma base ainda não indexada mostra o Bira sem
  // achar nada, e a conclusão errada seria "a base nova é ruim".
  if (naoIndexadas.value > 0) {
    confirmando.value = 'sem_indice';
    return;
  }
  if (!testado.value) {
    confirmando.value = 'sem_teste';
    return;
  }
  if (!naJanela.value) {
    confirmando.value = 'fora_da_janela';
    frase.value = '';
    return;
  }
  publicarAgora();
};

// Não bloqueia: avisa e segue se a pessoa insistir, como as outras guardas. Cai no teste,
// que é a guarda seguinte.
const pularIndice = () => {
  confirmando.value = null;
  if (!testado.value) {
    confirmando.value = 'sem_teste';
    return;
  }
  if (!naJanela.value) {
    confirmando.value = 'fora_da_janela';
    frase.value = '';
    return;
  }
  publicarAgora();
};

const pularTeste = () => {
  confirmando.value = null;
  if (!naJanela.value) {
    confirmando.value = 'fora_da_janela';
    frase.value = '';
    return;
  }
  publicarAgora();
};

const liberado = computed(
  () => frase.value.trim().toLowerCase() === FRASE_LIBERADORA
);

// As telas de Configurações vivem dentro de um <keep-alive>: sair e voltar NÃO remonta o
// componente, então `onMounted` não roda de novo e a tela volta com o estado de antes. Depois de
// publicar, isso significa uma tela achando que ainda existe um rascunho que já subiu, e todo
// salvamento batendo em 422. Recarregar ao reativar é o que mantém a tela e o servidor falando
// da mesma coisa.
let jaMontou = false;

onActivated(() => {
  if (!jaMontou) {
    jaMontou = true;
    return;
  }
  carregar();
});

onMounted(carregar);
</script>

<template>
  <SettingsLayout
    :is-loading="carregando"
    :loading-message="$t('BASE_PUBLICAR.HEADER')"
  >
    <template #body>
      <div class="flex flex-col gap-3.5 w-full min-w-0">
        <div class="flex items-baseline gap-2.5">
          <button
            type="button"
            class="text-[15px] text-n-slate-11 hover:text-n-slate-12"
            @click="irPara(voltarPara)"
          >
            {{
              veioDoComportamento
                ? $t('BASE_PUBLICAR.VOLTAR_VOZ')
                : $t('BASE_PUBLICAR.VOLTAR')
            }}
          </button>
          <h1 class="text-lg font-semibold text-n-slate-12">
            {{
              veioDoComportamento
                ? $t('BASE_PUBLICAR.HEADER_VOZ')
                : $t('BASE_PUBLICAR.HEADER')
            }}
          </h1>
        </div>

        <!-- Nada para publicar: some a tela inteira, fica só o caminho de volta. -->
        <div
          v-if="!temRascunho || pendentes === 0"
          class="rounded-md border border-n-weak bg-n-solid-1 px-5 py-6 flex flex-col items-start gap-3"
        >
          <p class="text-sm text-n-slate-11">
            {{
              veioDoComportamento
                ? $t('BASE_PUBLICAR.VAZIO_VOZ')
                : $t('BASE_PUBLICAR.VAZIO')
            }}
          </p>
          <Button
            :label="
              veioDoComportamento
                ? $t('BASE_PUBLICAR.VOLTAR_EDITAR_VOZ')
                : $t('BASE_PUBLICAR.VOLTAR_EDITAR')
            "
            variant="faded"
            color="slate"
            size="sm"
            @click="irPara(voltarPara)"
          />
        </div>

        <template v-else>
          <!-- A decisão fica no TOPO e gruda ali. Embaixo, ela dependia de rolar a lista de
               mudanças inteira para ser encontrada, e o que a pessoa vem fazer nesta tela é
               justamente decidir. -->
          <div
            class="sticky top-0 z-30 rounded-md border border-solid border-n-weak bg-n-solid-2 px-[18px] py-3.5 flex flex-wrap items-center gap-x-4 gap-y-2"
          >
            <div class="flex-1 min-w-[280px]">
              <p class="text-[12.5px] text-n-slate-11">
                {{
                  $t('BASE_PUBLICAR.ESCOPO', {
                    n: publicacao.em_andamento || 0,
                  })
                }}
              </p>
              <p
                class="text-xs mt-1"
                :class="naJanela ? 'text-n-slate-10' : 'text-n-amber-11'"
              >
                {{
                  naJanela
                    ? $t('BASE_PUBLICAR.JANELA_OK')
                    : $t('BASE_PUBLICAR.JANELA_FORA')
                }}
              </p>
            </div>
            <Button
              :label="$t('BASE_PUBLICAR.VOLTAR_EDITAR')"
              variant="faded"
              color="slate"
              size="sm"
              @click="irPara(voltarPara)"
            />
            <Button
              :label="$t('BASE_PUBLICAR.PUBLICAR', { n: pendentes })"
              color="amber"
              size="sm"
              :is-loading="publicando"
              @click="tentarPublicar"
            />
          </div>

          <!-- As confirmações ficam GRUDADAS na barra de decisão, e não no fim da página.
               Elas nasceram lá embaixo e o efeito era: a pessoa clicava em Publicar, o
               painel abria fora da vista e a tela parecia não ter feito nada. Aconteceu em
               18/09, com a barra no topo e a confirmação a 350 linhas de distância. -->
          <div
            v-if="confirmando"
            class="sticky top-[76px] z-20 flex flex-col gap-2"
          >
            <!-- 5a: publicar antes da indexação terminar -->
            <div
              v-if="confirmando === 'sem_indice'"
              class="rounded-md border border-n-amber-6 bg-n-amber-2 px-4 py-3 flex flex-col gap-2"
            >
              <p class="text-sm font-medium text-n-slate-12">
                {{ $t('BASE_PUBLICAR.SEM_INDICE.TITULO', { n: naoIndexadas }) }}
              </p>
              <p class="text-[12.5px] text-n-slate-11">
                {{ $t('BASE_PUBLICAR.SEM_INDICE.CORPO') }}
              </p>
              <div class="flex flex-wrap gap-2 mt-1">
                <Button
                  :label="$t('BASE_PUBLICAR.SEM_INDICE.ESPERAR')"
                  color="amber"
                  size="sm"
                  @click="confirmando = null"
                />
                <Button
                  :label="$t('BASE_PUBLICAR.SEM_INDICE.ASSIM_MESMO')"
                  variant="faded"
                  color="slate"
                  size="sm"
                  @click="pularIndice"
                />
              </div>
            </div>

            <!-- 5b: publicar sem ter testado -->
            <div
              v-if="confirmando === 'sem_teste'"
              class="rounded-md border border-n-amber-6 bg-n-amber-2 px-4 py-3 flex flex-col gap-2"
            >
              <p class="text-sm font-medium text-n-slate-12">
                {{ $t('BASE_PUBLICAR.SEM_TESTE.TITULO') }}
              </p>
              <p class="text-[12.5px] text-n-slate-11">
                {{ $t('BASE_PUBLICAR.SEM_TESTE.CORPO') }}
              </p>
              <div class="flex flex-wrap gap-2 mt-1">
                <Button
                  :label="$t('BASE_PUBLICAR.SEM_TESTE.TESTAR')"
                  color="amber"
                  size="sm"
                  @click="confirmando = null"
                />
                <Button
                  :label="$t('BASE_PUBLICAR.SEM_TESTE.ASSIM_MESMO')"
                  variant="faded"
                  color="slate"
                  size="sm"
                  @click="pularTeste"
                />
              </div>
            </div>

            <!-- 5c: fora da janela combinada -->
            <div
              v-if="confirmando === 'fora_da_janela'"
              class="rounded-md border border-n-amber-6 bg-n-amber-2 px-4 py-3 flex flex-col gap-2"
            >
              <p class="text-sm font-medium text-n-slate-12">
                {{ $t('BASE_PUBLICAR.FORA.TITULO') }}
              </p>
              <p class="text-[12.5px] text-n-slate-11">
                {{ $t('BASE_PUBLICAR.FORA.CORPO') }}
              </p>
              <input
                v-model="frase"
                type="text"
                class="reset-base w-[240px] max-w-full rounded-[5px] border border-n-strong bg-n-solid-2 px-2.5 py-1.5 text-[13px] text-n-slate-12"
                :placeholder="FRASE_LIBERADORA"
              />
              <div class="flex flex-wrap gap-2 mt-1">
                <Button
                  :label="$t('BASE_PUBLICAR.FORA.CANCELAR')"
                  variant="faded"
                  color="slate"
                  size="sm"
                  @click="confirmando = null"
                />
                <Button
                  :label="$t('BASE_PUBLICAR.PUBLICAR', { n: pendentes })"
                  :color="liberado ? 'amber' : 'slate'"
                  :variant="liberado ? 'solid' : 'faded'"
                  size="sm"
                  :disabled="!liberado"
                  :is-loading="publicando"
                  @click="publicarAgora"
                />
              </div>
            </div>
          </div>

          <!-- Resumo do que vai ao ar -->
          <div
            class="rounded-md border border-n-amber-6 bg-n-amber-2 px-[18px] py-3.5 flex flex-col gap-2.5"
          >
            <p class="text-xs text-n-amber-11/70">
              <template v-if="veioDoComportamento">
                {{
                  $t('BASE_PUBLICAR.META_VOZ', {
                    campos: comportamento.campos
                      .map(campo =>
                        $t(`BASE_PUBLICAR.VOZ.CAMPO.${campo.toUpperCase()}`)
                      )
                      .join(', '),
                  })
                }}
              </template>
              <template v-else>
                {{
                  $t('BASE_PUBLICAR.META', {
                    n: respostasMudadas,
                    de: respostas.total_antes,
                    para: respostas.total_depois,
                  })
                }}
              </template>
            </p>
            <div
              v-if="!veioDoComportamento"
              class="flex flex-wrap items-center gap-2"
            >
              <span :class="[MONO, CHIP_ESTADO.novo]">
                {{ $t('BASE_PUBLICAR.ENTRAM', { n: respostas.entraram }) }}
              </span>
              <span :class="[MONO, CHIP_ESTADO.alterado]">
                {{ $t('BASE_PUBLICAR.MUDAM', { n: respostas.mudaram }) }}
              </span>
              <span :class="[MONO, CHIP_ESTADO.a_sair]">
                {{ $t('BASE_PUBLICAR.SAEM', { n: respostas.saem }) }}
              </span>
            </div>
            <div class="flex flex-wrap items-center gap-2.5">
              <span class="text-[12.5px] text-n-slate-11 whitespace-nowrap">
                {{ $t('BASE_PUBLICAR.NOME') }}
              </span>
              <input
                v-model="rotulo"
                type="text"
                class="reset-base w-[380px] max-w-full min-w-0 rounded-[5px] border border-n-amber-6 bg-n-solid-2 px-2.5 py-1.5 text-[13px] text-n-slate-12"
                :placeholder="$t('BASE_PUBLICAR.NOME_DICA')"
                @blur="salvarRotulo"
              />
            </div>
          </div>

          <!-- O que muda no jeito de falar. Mesma leitura das respostas: o texto de hoje à
               esquerda, o novo à direita. -->
          <section
            v-if="veioDoComportamento && comportamento.campos.length"
            class="rounded-md border border-solid border-n-weak bg-n-solid-1 overflow-hidden"
          >
            <div class="px-[18px] py-3 border-b border-solid border-n-weak">
              <h2 class="text-sm font-semibold text-n-slate-12">
                {{ $t('BASE_PUBLICAR.VOZ.TITULO') }}
              </h2>
              <p class="mt-0.5 text-[12.5px] text-n-slate-11">
                {{ $t('BASE_PUBLICAR.VOZ.AJUDA') }}
              </p>
            </div>
            <div
              v-for="campo in comportamento.campos"
              :key="campo"
              class="px-[18px] py-3 border-b border-solid border-n-weak last:border-b-0"
            >
              <span
                class="text-[13px] font-semibold text-n-slate-12 flex items-center gap-2"
              >
                {{ $t(`BASE_PUBLICAR.VOZ.CAMPO.${campo.toUpperCase()}`) }}
                <span :class="MONO" class="bg-n-amber-3 text-n-amber-11">
                  {{ $t('BASE_PUBLICAR.VOZ.MUDOU') }}
                </span>
              </span>
              <div class="mt-2 grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div class="min-w-0 flex flex-col gap-1">
                  <span
                    class="font-mono text-[10px] uppercase tracking-[.06em] text-n-slate-10"
                  >
                    {{ $t('BASE_PUBLICAR.HOJE') }}
                  </span>
                  <p
                    class="rounded-[5px] border border-solid border-n-weak bg-n-alpha-1 px-3 py-2 text-[12.5px] leading-relaxed text-n-slate-11 whitespace-pre-line break-words max-h-[200px] overflow-y-auto"
                  >
                    {{
                      comportamento.antes[campo] ||
                      $t('BASE_PUBLICAR.VOZ.VAZIO')
                    }}
                  </p>
                </div>
                <div class="min-w-0 flex flex-col gap-1">
                  <span
                    class="font-mono text-[10px] uppercase tracking-[.06em] text-n-amber-11"
                  >
                    {{ $t('BASE_PUBLICAR.DEPOIS') }}
                  </span>
                  <p
                    class="rounded-[5px] border border-solid border-n-amber-6 bg-n-amber-2 px-3 py-2 text-[12.5px] leading-relaxed text-n-slate-12 whitespace-pre-line break-words max-h-[200px] overflow-y-auto"
                  >
                    {{
                      comportamento.depois[campo] ||
                      $t('BASE_PUBLICAR.VOZ.VAZIO')
                    }}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <div class="flex gap-3 items-stretch">
            <!-- O que muda, item a item. A lista é só da base; o que muda na voz tem bloco
                 próprio acima, e o painel de TESTE vale para os dois: publicar a voz é a mudança
                 que mexe em todas as respostas de uma vez, e é a que mais precisa ser testada. -->
            <div
              v-if="!veioDoComportamento"
              class="flex-1 min-w-0 flex flex-col gap-2.5"
            >
              <div
                v-for="item in respostas.itens"
                :key="item.numero_card"
                class="rounded-md border border-n-weak bg-n-solid-1 px-4 py-3 flex flex-col gap-2 min-w-0"
              >
                <div class="flex items-center gap-2 flex-nowrap min-w-0">
                  <span
                    :class="[MONO, CHIP_ESTADO[item.estado]]"
                    class="flex-none"
                  >
                    {{
                      $t(`BASE_PUBLICAR.ESTADO.${item.estado.toUpperCase()}`)
                    }}
                  </span>
                  <span
                    class="text-[13px] font-semibold text-n-slate-12 min-w-0 truncate"
                  >
                    {{ item.pergunta }}
                  </span>
                </div>
                <p
                  v-if="
                    item.pergunta_antes && item.pergunta_antes !== item.pergunta
                  "
                  class="text-[12px] text-n-slate-10 break-words"
                >
                  {{
                    $t('BASE_PUBLICAR.ANTES_CHAMAVA', {
                      nome: item.pergunta_antes,
                    })
                  }}
                </p>

                <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div class="min-w-0 flex flex-col gap-1">
                    <span
                      class="font-mono text-[10px] uppercase tracking-[.06em] text-n-slate-10"
                    >
                      {{ $t('BASE_PUBLICAR.HOJE') }}
                    </span>
                    <p
                      v-if="item.antes"
                      class="rounded-[5px] border border-n-weak bg-n-alpha-1 px-3 py-2 text-[12.5px] leading-relaxed text-n-slate-11 whitespace-pre-line break-words max-h-[200px] overflow-y-auto"
                    >
                      {{ item.antes }}
                    </p>
                    <p v-else class="text-[12.5px] text-n-slate-10 italic">
                      {{ $t('BASE_PUBLICAR.NAO_EXISTIA') }}
                    </p>
                  </div>
                  <div class="min-w-0 flex flex-col gap-1">
                    <span
                      class="font-mono text-[10px] uppercase tracking-[.06em] text-n-amber-11"
                    >
                      {{ $t('BASE_PUBLICAR.DEPOIS') }}
                    </span>
                    <p
                      v-if="item.depois"
                      class="rounded-[5px] border border-n-amber-6 bg-n-amber-2 px-3 py-2 text-[12.5px] leading-relaxed text-n-slate-12 whitespace-pre-line break-words max-h-[200px] overflow-y-auto"
                    >
                      {{ item.depois }}
                    </p>
                    <p v-else class="text-[12.5px] text-n-ruby-11">
                      {{ $t('BASE_PUBLICAR.DEIXA_DE_RESPONDER') }}
                    </p>
                  </div>
                </div>

                <p
                  v-if="item.autor || item.quando"
                  class="font-mono text-[10px] tracking-[.06em] text-n-slate-10"
                >
                  {{ item.autor || $t('BASE_PUBLICAR.SEM_AUTOR') }}
                  <template v-if="item.quando">
                    · {{ hora(item.quando) }}</template
                  >
                </p>
              </div>
            </div>

            <!-- Testar antes -->
            <div
              class="flex flex-col gap-2.5"
              :class="
                veioDoComportamento
                  ? 'flex-1 max-w-[560px]'
                  : 'w-[400px] flex-none'
              "
            >
              <div
                class="rounded-md border border-n-weak bg-n-solid-1 px-4 py-3 flex flex-col gap-2.5"
              >
                <div class="flex items-baseline justify-between gap-2">
                  <h2 class="text-sm font-semibold text-n-slate-12">
                    {{ $t('BASE_PUBLICAR.TESTAR') }}
                  </h2>
                  <span
                    class="font-mono text-[10px] tracking-[.06em]"
                    :class="testado ? 'text-n-teal-11' : 'text-n-slate-10'"
                  >
                    {{ $t('BASE_PUBLICAR.TESTES', { n: testes }) }}
                  </span>
                </div>
                <p class="text-[12.5px] text-n-slate-10">
                  {{ $t('BASE_PUBLICAR.TESTAR_DICA') }}
                </p>
                <p
                  v-if="outroRascunho"
                  class="text-[12.5px] text-n-amber-11 leading-relaxed"
                >
                  {{
                    $t('BASE_PUBLICAR.TESTAR_OUTRO', {
                      assunto: $t(
                        `BASE_PUBLICAR.ASSUNTO.${outroRascunho.toUpperCase()}`
                      ),
                    })
                  }}
                </p>
                <textarea
                  v-model="teste.texto"
                  class="m-0 border-solid w-full min-w-0 min-h-[76px] resize-y leading-relaxed rounded-[5px] border border-n-strong bg-n-solid-2 px-2.5 py-2 text-[13px] text-n-slate-12"
                  :placeholder="$t('BASE_PUBLICAR.PERGUNTA')"
                />
                <div
                  class="grid grid-cols-[110px_1fr] items-center gap-x-2.5 gap-y-2"
                >
                  <span class="text-[12.5px] text-n-slate-11 whitespace-nowrap">
                    {{ $t('BASE_PUBLICAR.PLANO') }}
                  </span>
                  <FiltroSelect
                    v-model="teste.plano"
                    :opcoes="opcoesPlano"
                    largura-cheia
                  />
                  <span class="text-[12.5px] text-n-slate-11 whitespace-nowrap">
                    {{ $t('BASE_PUBLICAR.SITUACAO') }}
                  </span>
                  <FiltroSelect
                    v-model="teste.situacao"
                    :opcoes="opcoesSituacao"
                    largura-cheia
                  />
                </div>
                <Button
                  :label="$t('BASE_PUBLICAR.RODAR')"
                  color="slate"
                  variant="faded"
                  size="sm"
                  :is-loading="rodando"
                  :disabled="!teste.texto.trim()"
                  @click="rodarTeste"
                />
              </div>

              <div
                v-for="lado in ['publicado', 'rascunho']"
                v-show="resultado"
                :key="lado"
                class="rounded-md border px-4 py-3 flex flex-col gap-1.5"
                :class="
                  lado === 'publicado'
                    ? 'border-n-weak bg-n-solid-1'
                    : 'border-n-amber-6 bg-n-amber-2'
                "
              >
                <span
                  class="font-mono text-[10px] uppercase tracking-[.06em]"
                  :class="
                    lado === 'publicado' ? 'text-n-slate-10' : 'text-n-amber-11'
                  "
                >
                  {{ $t(`BASE_PUBLICAR.LADO.${lado.toUpperCase()}`) }}
                </span>
                <p
                  v-if="resultado?.[lado]?.erro"
                  class="text-[12.5px] text-n-ruby-11"
                >
                  {{
                    $t('BASE_PUBLICAR.TESTE_ERRO', {
                      motivo: resultado[lado].erro,
                    })
                  }}
                </p>
                <template v-else-if="resultado?.[lado]">
                  <p
                    class="text-[12.5px] leading-relaxed text-n-slate-12 whitespace-pre-line break-words"
                  >
                    {{ resultado[lado].texto || resultado[lado].motivo }}
                  </p>
                  <!-- Qual resposta embasou e por quê: sem isso o teste vira adivinhação, e
                       "por que ele respondeu isso?" continua sem dono. -->
                  <p
                    v-for="trecho in (resultado[lado].trechos || []).slice(
                      0,
                      2
                    )"
                    :key="trecho.id"
                    class="font-mono text-[10px] tracking-[.06em] text-n-slate-10 break-words"
                  >
                    {{ trecho.numero }} · {{ trecho.titulo }}
                    <template v-if="trecho.motivo">
                      · {{ trecho.motivo }}</template
                    >
                  </p>
                </template>
                <p v-else class="text-[12.5px] text-n-slate-10">
                  {{ $t('BASE_PUBLICAR.SEM_RASCUNHO_LADO') }}
                </p>
              </div>
            </div>
          </div>
        </template>
      </div>
    </template>
  </SettingsLayout>
</template>
